import { Organization } from "@/data/Organization";
import { BillingPlans } from "@/data/billing/BillingPlans";
import { BillingSettings } from "@/data/settings/BillingSettings";
import { CurrentUser } from "@/data/users/CurrentUser";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Zoom from "@mui/material/Zoom";
import { Theme } from "klayowebshared";
import { AppContext } from "../../common/AppContext";
import { Utils } from "../../common/Utils";
import { ErrorModel } from "../../common/models/ErrorModel";
import { Alert } from "../../components/Alert";
import { BillingGroupCard } from "../../components/BillingGroupCard";
import { BillingSelectionCard } from "../../components/BillingSelectionCard";
import { Button } from "../../components/Button";
import { ConfirmationBox } from "../../components/ConfirmationBox";
import { PillButtons } from "../../components/PillButtons";
import { ViewComponent } from "../../components/ViewComponent";
import { BillingPlan } from "../../data/billing/BillingPlan";
import { SubmitBilling } from "../../data/settings/SubmitBilling";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import BackIcon from "../../resources/images/icons-arrow-left.svg";
import ClapIcon from "../../resources/images/icons-clap.svg";
import CloseIcon from "../../resources/images/icons-close.svg";
import Logo from "../../resources/images/logo.svg";
import { BillingContact } from "./BillingContact";
import { BillingDetails } from "./BillingDetails";
import { CreditCardPayment } from "./CreditCardPayment";
import { InvoicePayment } from "./InvoicePayment";

interface BillingPlansDialogProps extends WithLocaleProps {
  organization?: Organization | null;
  user?: CurrentUser | null;
  billingPlans?: BillingPlans;
  existingBillingDetails?: BillingSettings;
  onClose?: () => void;
  onLoadBillingPlans?: (updateCache?: boolean) => void;
  onLoadBillingDetails?: (updateCache?: boolean) => void;
  onContactUs?: (e: any, path: string) => void;
  theme: typeof Theme;
}

class BillingPlansDialogBase extends ViewComponent<BillingPlansDialogProps> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;
  existingGroup?: BillingPlan | null;

  constructor(props: any) {
    super(props);

    this.existingGroup = this.getExistingGroup(props.billingPlans, props.existingBillingDetails);

    this.state = {
      selectedGroup: null,
      selectedPlan: null,
      step: 0,
      paymentMethod:
        props.existingBillingDetails &&
        props.existingBillingDetails.paymentMethod !== BillingPlan.paymentMethod.none
          ? props.existingBillingDetails.paymentMethod
          : BillingPlan.paymentMethod.creditCard,
      billingCycle:
        props.existingBillingDetails &&
        props.existingBillingDetails.cycle !== BillingPlan.cycle.none
          ? props.existingBillingDetails.cycle
          : BillingPlan.cycle.monthly,
      error: null,
      submitting: false,
      submitted: false,
      billingContact: {},
      billingDetails: {},
      isNewPlan: this.isNewPlan(props.existingBillingDetails),
      allowChange: props.existingBillingDetails ? !props.existingBillingDetails.isCustomPlan : true,
      creditCardDetails: {},
      ewaySecureFieldCode: null,
      creditCardAttemptId: 0
    };
  }

  componentDidMount() {
    const { onLoadBillingPlans, onLoadBillingDetails } = this.props;
    onLoadBillingPlans?.();
    onLoadBillingDetails?.();
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    super.componentDidUpdate(prevProps, prevState);

    if (
      this.props.billingPlans !== prevProps.billingPlans ||
      this.props.existingBillingDetails !== prevProps.existingBillingDetails
    ) {
      if (this.props.existingBillingDetails && this.props.billingPlans) {
        this.existingGroup = this.getExistingGroup(
          this.props.billingPlans,
          this.props.existingBillingDetails
        );
        this.setState({ isNewPlan: this.isNewPlan(this.props.existingBillingDetails) });
      }
    }
  }

  isNewPlan(existingBillingDetails: any) {
    if (!existingBillingDetails) return true;
    const { organization } = this.props;
    if (!organization) return true;

    return (
      /*organization.isTrial || */ (existingBillingDetails.cycle === BillingPlan.cycle.none ||
        existingBillingDetails.paymentMethod === BillingPlan.paymentMethod.none) === true
    );
  }

  getExistingGroup(billingPlans: BillingPlans, existingBillingDetails: any) {
    if (!billingPlans || !existingBillingDetails) return null;
    return billingPlans.groups.find((g) => g.planGroupId === existingBillingDetails.planGroupId);
  }

  onGroupSelect(e: any, selectedGroup?: BillingPlan) {
    const { onContactUs } = this.props;
    const { paymentMethod, billingCycle } = this.state as any;

    if (selectedGroup?.isCustom) onContactUs?.(e, "/settings/billing");
    else
      this.setState({
        selectedGroup,
        selectedPlan: selectedGroup?.getPlan(paymentMethod, billingCycle),
        error: null
      });
  }

  onNextStep(e: any) {
    const { isNewPlan, step, billingContact, billingDetails } = this.state as any;
    if (isNewPlan) {
      if (step === 1) {
        if (!this.validateBillingContactDetails(billingContact)) return false;
        if (!this.validateBillingDetails(billingDetails)) return false;
      }
      this.setState({ step: step + 1, error: null });
    } else this.onSubmit(e);
  }

  onPrevStep(e: any) {
    const { step } = this.state as any;
    this.setState({ step: step - 1, error: null });
  }

  onPaymentMethodChange(e: any, paymentMethod: any) {
    const { selectedGroup, billingCycle } = this.state as any;
    const newBillingCycle =
      paymentMethod === BillingPlan.paymentMethod.creditCard
        ? BillingPlan.cycle.monthly
        : billingCycle === BillingPlan.cycle.monthly
          ? BillingPlan.cycle.quarterly
          : billingCycle;
    this.setState({
      paymentMethod,
      selectedPlan: selectedGroup ? selectedGroup.getPlan(paymentMethod, newBillingCycle) : null,
      billingCycle: newBillingCycle,
      error: null
    });
  }

  onBillingCycleChange(e: any, billingCycle: any) {
    const { selectedGroup, paymentMethod } = this.state as any;
    this.setState({
      billingCycle,
      selectedPlan: selectedGroup ? selectedGroup.getPlan(paymentMethod, billingCycle) : null
    });
  }

  onResetGroup(e: any) {
    this.setState({ selectedGroup: null, selectedPlan: null, step: 0, error: null });
  }

  onBillingContactChange(e: any, billingContact: any) {
    this.setState({ billingContact });
  }

  onBillingDetailsChange(e: any, billingDetails: any) {
    this.setState({ billingDetails });
  }

  onCreditCardChange(creditCardDetails: any, ewaySecureFieldCode: any) {
    this.setState({ creditCardDetails, ewaySecureFieldCode });
  }

  unBlurAndSubmit(e: any) {
    //Eqay fields only fire event callback on blur
    setTimeout((t: any) => this.onSubmit(e), 500);
  }

  onSubmit(e: any) {
    const {
      isNewPlan,
      selectedPlan,
      billingContact,
      billingDetails,
      creditCardDetails,
      ewaySecureFieldCode,
      paymentMethod
    } = this.state as any;

    if (isNewPlan) {
      if (
        paymentMethod === BillingPlan.paymentMethod.invoice &&
        !this.validateInvoiceDetails({ billingContact, billingDetails })
      )
        return false;
      if (paymentMethod === BillingPlan.paymentMethod.creditCard) {
        if (!this.validateCreditCardDetails({ creditCardDetails, billingContact, billingDetails }))
          return false;
      }
    }

    (this.context as any)?.setLoading("submitting", true);
    this.setState({ error: null, submitting: true });

    if (isNewPlan) {
      SubmitBilling.post(this.context as any, {
        billingPlanId: selectedPlan.billingPlanId,
        securedFieldsCode: ewaySecureFieldCode,
        contactFirstName: billingContact.firstName,
        contactLastName: billingContact.lastName,
        contactEmail: billingContact.email,
        contactPhone: billingContact.phone,
        companyName: billingDetails.company,
        street1: billingDetails.billingAddress,
        country: billingDetails.country.name,
        city: billingDetails.city,
        state: billingDetails.state,
        postalCode: billingDetails.zipCode
      })
        .then((n) => {
          this.setState({ error: null, submitted: true });
        })
        .catch((e) => {
          const error: any = new ErrorModel(ErrorModel.Severity.FAIL, e);
          let newState: any = { error: error.message };
          if (error.details && error.details.creditCardTransactionAttempted)
            newState = {
              ...newState,
              creditCardDetails: {},
              creditCardAttemptId: Utils.generateUniqueId()
            };
          this.setState(newState);
        })
        .finally(() => {
          (this.context as any)?.setLoading("submitting", false);
          this.setState({ submitting: false });
        });
    } else {
      SubmitBilling.put(this.context as any, {
        billingPlanId: selectedPlan.billingPlanId
      })
        .then((n) => {
          this.setState({ error: null, submitted: true });
        })
        .catch((e) => {
          const error = ErrorModel.parseServerError(e);
          this.setState({ error, creditCardAttemptId: Utils.generateUniqueId() });
        })
        .finally(() => {
          (this.context as any)?.setLoading("submitting", false);
          this.setState({ submitting: false });
        });
    }
  }

  validateInvoiceDetails(invoiceDetails: any) {
    const { billingContact, billingDetails } = invoiceDetails;
    if (!this.validateBillingContactDetails(billingContact)) return false;
    if (!this.validateBillingDetails(billingDetails)) return false;
    return true;
  }

  validateBillingContactDetails(billingContact: any) {
    const { translate } = this.props;
    if (!billingContact.firstName) {
      this.setError(translate("billing.errorFirstName"));
      return false;
    } else if (!billingContact.lastName) {
      this.setError(translate("billing.errorLastName"));
      return false;
    } else if (!Utils.isValidEmail(billingContact.email)) {
      this.setError(translate("billing.errorEmail"));
      return false;
    } else if (!billingContact.phone) {
      this.setError(translate("billing.errorPhone"));
      return false;
    }
    return true;
  }

  validateBillingDetails(billingDetails: any) {
    const { translate } = this.props;
    if (!billingDetails.company) {
      this.setError(translate("billing.errorCompany"));
      return false;
    } else if (!billingDetails.billingAddress) {
      this.setError(translate("billing.errorBillingAddress"));
      return false;
    } else if (!billingDetails.country) {
      this.setError(translate("billing.errorCountry"));
      return false;
    } else if (!billingDetails.city) {
      this.setError(translate("billing.errorCity"));
      return false;
    } else if (!billingDetails.state) {
      this.setError(translate("billing.errorState"));
      return false;
    } else if (!billingDetails.zipCode) {
      this.setError(translate("billing.errorZipCode"));
      return false;
    }
    return true;
  }

  validateCreditCardDetails(ccDetails: any) {
    const { translate } = this.props;
    const { creditCardDetails, billingContact, billingDetails } = ccDetails;
    const { nameValid, cardValid, expiryValid, cvcValid } = creditCardDetails;

    if (nameValid !== true) {
      this.setError(translate("billing.errorCreditCardName"));
      return false;
    }
    if (cardValid !== true) {
      this.setError(translate("billing.errorCreditCardNumber"));
      return false;
    }
    if (expiryValid !== true) {
      this.setError(translate("billing.errorCreditCardExpiry"));
      return false;
    }
    if (cvcValid !== true) {
      this.setError(translate("billing.errorCreditCardCvc"));
      return false;
    }
    if (!this.validateBillingContactDetails(billingContact)) return false;
    if (!this.validateBillingDetails(billingDetails)) return false;
    return true;
  }

  setError(error: any) {
    this.setState({ error });
    if (error) window.scrollTo(0, 0);
  }

  render() {
    const { organization, user, billingPlans, onClose, translate } = this.props;
    const {
      selectedGroup,
      selectedPlan,
      step,
      paymentMethod,
      billingCycle,
      error,
      submitting,
      submitted,
      isNewPlan,
      allowChange,
      creditCardAttemptId,
      billingContact,
      billingDetails
    } = this.state as any;

    return (
      <Dialog
        className='klayo-billingplandialog'
        onClose={onClose as any}
        open={true}
        fullScreen={true}
      >
        <AppBar className='klaro_employeeprofile-appbar' elevation={0}>
          <Toolbar>
            <Logo className='klaro-appbar_logo' />
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <Button
                autoFocus
                label={translate("common.close")}
                variant='outlined'
                size='md'
                startIcon={<CloseIcon />}
                onClick={onClose as any}
              />
            </Box>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ marginTop: "64px", backgroundColor: "#F8F8FE" }}>
          <div className='klayo-billingplandialog_content'>
            {submitted && (
              <ConfirmationBox
                icon={<ClapIcon />}
                heading={translate("billing.youreSet")}
                message={translate("billing.accountReadyForAction")}
                action={translate("common.close")}
                onAction={onClose as any}
              />
            )}

            {!submitted && (
              <div>
                {(step === 0 || selectedGroup === null) && (
                  <Stack direction='column' spacing={2}>
                    <h1>{translate("billing.choosePerfectPlan")}</h1>

                    {error && (
                      <Alert severity='error' scrollTo={true}>
                        {error}
                      </Alert>
                    )}

                    <h2>{translate("billing.ourPlans")}</h2>
                    <Stack direction='column' spacing={5} sx={{ marginTop: "30px!important" }}>
                      {billingPlans &&
                        billingPlans.groups
                          .filter((g) => g.name !== "Enterprise")
                          .map((group, key: number) => (
                            <BillingGroupCard
                              key={key}
                              group={group}
                              selected={selectedGroup === group}
                              existing={
                                !!(
                                  this.existingGroup &&
                                  this.existingGroup.planGroupId === group.planGroupId
                                )
                              }
                              disabled={
                                !allowChange ||
                                (this.existingGroup &&
                                  this.existingGroup.planGroupId === group.planGroupId) ||
                                !group.isAllowed
                              }
                              onClick={this.onGroupSelect.bind(this)}
                            />
                          ))}

                      <div style={{ textAlign: "center" }}>
                        <Button
                          label={translate("billing.choosePlan")}
                          variant='filled'
                          disabled={selectedGroup === null || !allowChange}
                          onClick={this.onNextStep.bind(this)}
                        />
                      </div>
                    </Stack>

                    <div>
                      <Divider sx={{ margin: "40px 0" }} />
                      <h2>{translate("billing.needMore")}</h2>
                      {billingPlans && (
                        <BillingGroupCard
                          group={billingPlans.enterprisePlan}
                          onClick={this.onGroupSelect.bind(this)}
                        />
                      )}
                    </div>
                  </Stack>
                )}

                {step === 1 && selectedGroup && selectedGroup !== BillingPlan.group.enterprise && (
                  <Stack direction='column' spacing={4}>
                    <h1>{translate("billing.billingDetails")}</h1>
                    {error && (
                      <Alert severity='error' scrollTo={true}>
                        {error}
                      </Alert>
                    )}

                    <BillingContact
                      user={user}
                      organization={organization}
                      billingContact={billingContact}
                      onChange={this.onBillingContactChange.bind(this) as any}
                      disabled={submitting}
                    />

                    <Divider sx={{ margin: "30px 0 0 0" }} />

                    <BillingDetails
                      user={user}
                      organization={organization}
                      billingDetails={billingDetails}
                      onChange={this.onBillingDetailsChange.bind(this) as any}
                      disabled={submitting}
                    />

                    <Stack
                      direction='row'
                      justifyContent='center'
                      spacing={2}
                      sx={{ paddingTop: "32px" }}
                    >
                      <Button
                        label={translate("common.back")}
                        variant='outlined'
                        startIcon={<BackIcon />}
                        onClick={this.onPrevStep.bind(this)}
                        disabled={submitting}
                      />

                      <Button
                        label={translate("common.continue")}
                        variant='filled'
                        onClick={this.onNextStep.bind(this)}
                        disabled={submitting}
                      />
                    </Stack>
                  </Stack>
                )}

                {step === 2 && selectedGroup && selectedGroup !== BillingPlan.group.enterprise && (
                  <Stack direction='column' spacing={4}>
                    <h1>{translate("billing.selectPaymentMethod")}</h1>
                    <h2>{translate("billing.paymentDetails")}</h2>

                    {error && (
                      <Alert severity='error' scrollTo={true}>
                        {error}
                      </Alert>
                    )}

                    {isNewPlan && (
                      <PillButtons
                        label={translate("billing.paymentMethod")}
                        value={paymentMethod}
                        items={[
                          BillingPlan.paymentMethod.creditCard,
                          BillingPlan.paymentMethod.invoice
                        ]}
                        onChange={this.onPaymentMethodChange.bind(this) as any}
                        sx={{ alignSelf: "flex-start" }}
                      />
                    )}

                    <CreditCardPayment
                      onResetGroup={this.onResetGroup.bind(this) as any}
                      onChange={this.onCreditCardChange.bind(this) as any}
                      disabled={true}
                      attemptId={creditCardAttemptId}
                      visible={paymentMethod === BillingPlan.paymentMethod.creditCard}
                    />

                    <InvoicePayment
                      onResetGroup={this.onResetGroup.bind(this) as any}
                      billingCycle={billingCycle}
                      onBillingCycleChange={this.onBillingCycleChange.bind(this) as any}
                      visible={paymentMethod === BillingPlan.paymentMethod.invoice}
                    />

                    <Zoom in={true}>
                      <div>
                        <BillingSelectionCard
                          onResetGroup={this.onResetGroup.bind(this) as any}
                          group={selectedGroup}
                          plan={selectedPlan}
                        />
                      </div>
                    </Zoom>

                    <Stack
                      direction='row'
                      justifyContent='center'
                      spacing={2}
                      sx={{ paddingTop: "32px" }}
                    >
                      <Button
                        label={translate("common.back")}
                        variant='outlined'
                        startIcon={<BackIcon />}
                        onClick={this.onPrevStep.bind(this)}
                        disabled={submitting}
                      />
                      <Button
                        label={
                          paymentMethod === BillingPlan.paymentMethod.creditCard
                            ? translate("billing.payNow")
                            : translate("dialogs.submitRequest")
                        }
                        variant='filled'
                        onClick={
                          paymentMethod === BillingPlan.paymentMethod.creditCard
                            ? this.unBlurAndSubmit.bind(this)
                            : this.onSubmit.bind(this)
                        }
                        disabled={submitting}
                      />
                    </Stack>
                  </Stack>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

export const BillingPlansDialog = withLocale<BillingPlansDialogProps>(BillingPlansDialogBase);
